import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmSmallFleetDialog } from "@/app/Onboarding/Components/Dialogs/ConfirmSmallFleetDialog";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { RegionInput } from "@/app/Common/Components/RegionInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { onboardingCreateOrganisationMutation } from "@/app/Onboarding/GraphQL/onboardingCreateOrganisationMutation";
import { SubmitButtonInput } from "@/lib/Components/Form/Inputs/SubmitButtonInput";
import { HireUnitRadioButton } from "@/app/Common/Components/HireUnitRadioButton";
import { Currency, HireUnitType, Region } from "@/gql/graphql";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { createOrganisationValidation } from "@/app/Onboarding/Validators/createOrganisationValidation";
import { Formation } from "@/lib/Components/Form/Providers/Formation";
import { getRecaptchaToken } from "@/lib/Recaptcha/recaptcha";
import { Form } from "formik";
import { Button, LinkButton } from "@/components/catalyst/button";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/lib/Auth/useUser";
import { useResendVerification } from "@/lib/Auth/useResendVerification";
import { useState } from "react";
import { GlobeAltIcon, TruckIcon } from "@heroicons/react/24/outline";

export function OnboardingCreateOrgScreen() {
  const { mutateAsync } = useGqlMutation(onboardingCreateOrganisationMutation);
  const queryClient = useQueryClient();
  const user = useUser();
  const [isRentalCompany, setIsRentalCompany] = useState(false);
  const { openAsPromise: confirmSmallFleet } = useDialog(
    ConfirmSmallFleetDialog,
  );

  if (!user.email_verified) {
    return <VerifyEmailPrompt email={user.email} />;
  }

  if (!isRentalCompany) {
    return <IntentPrompt onContinue={() => setIsRentalCompany(true)} />;
  }

  return (
    <div className="flex max-w-sm flex-col items-center justify-center pb-24">
      <Formation
        validationSchema={toFormikValidationSchema(
          createOrganisationValidation,
        )}
        initialValues={{
          name: "",
          region: Region.Us,
          currency: Currency.Usd,
          hire_unit_type: HireUnitType.Day,
          fleet_size: null as unknown as number,
          description: "",
        }}
        onSubmit={async (values) => {
          if (values.fleet_size < 3) {
            const confirmed = await confirmSmallFleet({}).catch(() => false);

            if (!confirmed) {
              return;
            }
          }

          const recaptcha_token = await getRecaptchaToken(
            "onboarding_create_organisation",
          );

          const res = await mutateAsync({
            input: { ...values, recaptcha_token },
          });

          const { organisation_id, supplier_id } =
            res.onboardingCreateOrganisation;

          await queryClient.invalidateQueries({ queryKey: ["auth"] });

          window.location.href = `/org/${organisation_id}/supplier/${supplier_id}`;
        }}
      >
        <Form className="grid w-full grid-cols-1 gap-y-6">
          <TextInput name="name" label="Business name" autoFocus />
          <NumberInput
            name="fleet_size"
            label="Fleet size"
            inputProps={{ min: 1 }}
          />
          <TextAreaInput
            name="description"
            label="Fleet requirements"
            textAreaProps={{
              maxLength: 1000,
              rows: 3,
              placeholder: "Where do you want to move vehicles?",
            }}
          />
          <RegionInput label="Region" name="region" />
          <CurrencyTypeInput name="currency" label="Currency" />
          <HireUnitRadioButton name="hire_unit_type" label="Units" />

          <SubmitButtonInput>Create</SubmitButtonInput>
        </Form>
      </Formation>
      <div className="mt-2 flex w-full justify-center">
        <LinkButton plain className="w-full" to="/onboarding">
          Back
        </LinkButton>
      </div>
    </div>
  );
}

function IntentPrompt({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex w-full max-w-sm flex-col items-center justify-center gap-y-6 pb-24 text-center">
      <div className="flex flex-col gap-y-2">
        <h2 className="text-lg font-semibold">What brings you here?</h2>
        <p className="text-sm text-gray-600">
          Organisation accounts are for rental companies listing their fleet on
          Imoova. If you&apos;re a traveller looking to book a relocation, you
          don&apos;t need an organisation.
        </p>
      </div>
      <div className="flex w-full flex-col gap-y-3">
        <Button
          className="w-full"
          onClick={() => {
            window.location.assign("https://www.imoova.com");
          }}
        >
          <GlobeAltIcon className="h-5 w-5" />
          I&apos;m a traveller — book a relocation
        </Button>
        <Button outline className="w-full" onClick={onContinue}>
          <TruckIcon className="h-5 w-5" />
          I&apos;m a rental company — list my fleet
        </Button>
      </div>
      <LinkButton plain className="w-full" to="/onboarding">
        Back
      </LinkButton>
    </div>
  );
}

function VerifyEmailPrompt({ email }: { email: string }) {
  const { mutate: resend, isPending } = useResendVerification();

  return (
    <div className="flex max-w-sm flex-col items-center justify-center gap-y-6 pb-24 text-center">
      <div className="flex flex-col gap-y-2">
        <h2 className="text-lg font-semibold">Verify your email first</h2>
        <p className="text-sm text-gray-600">
          Before creating an organisation, please verify your email address. We
          sent a verification link to <strong>{email}</strong>.
        </p>
      </div>
      <Button className="w-full" disabled={isPending} onClick={() => resend()}>
        {isPending ? "Sending..." : "Resend verification email"}
      </Button>
      <LinkButton plain className="w-full" to="/onboarding">
        Back
      </LinkButton>
    </div>
  );
}
